import { Component, type ReactNode, type ErrorInfo } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { logger } from "@/lib/logger";

/** Consolidation context threaded in so a crash is actionable in App Insights. */
export interface LegislationContentErrorContext {
  documentId?: string;
  changeLaw?: string | null;
  asOf?: string | null;
  view?: string | null;
  timelineAt?: number | null;
}

interface Props {
  children: ReactNode;
  /** When this changes (e.g. the user re-selects a document / consolidation) the boundary auto-recovers. */
  resetKey: string;
  context: LegislationContentErrorContext;
}

interface State {
  hasError: boolean;
}

type PageTranslation = "edge" | "google" | "none";

/**
 * Which browser translator, if any, has rewritten the page. Translators swap
 * React-owned text nodes for their own, so React's next removeChild throws
 * NotFoundError (LAR-619). Edge stamps `_msttexthash` on what it translated;
 * Chrome's Google Translate adds `translated-ltr`/`translated-rtl` to <html>.
 */
const detectPageTranslation = (): PageTranslation => {
  if (document.querySelector("[_msttexthash]")) return "edge";
  const htmlClasses = document.documentElement.classList;
  if (
    htmlClasses.contains("translated-ltr") ||
    htmlClasses.contains("translated-rtl")
  ) {
    return "google";
  }
  return "none";
};

/**
 * Catches render crashes in the legislation document content region and logs
 * them to Application Insights. Without this, a render error (e.g. the LAR-306
 * removeChild crash) bubbles to React Router's generic "Unexpected Application
 * Error" page and never reaches telemetry, making prod incidents undiagnosable.
 * Placed below the toolbar/timeline so the reader can recover by navigating.
 */
class LegislationContentErrorBoundary extends Component<Props, State> {
  /**
   * React calls componentDidCatch once per error, and a translated page throws
   * one removeChild per removed text node in a single commit (323 exceptions in
   * 0.5 s for one prod crash), so only the first error of a crash is logged.
   */
  private crashLogged = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.crashLogged) return;
    this.crashLogged = true;
    logger.error("Legislation document content render crashed", {
      operation: "renderLegislationDocumentContent",
      error,
      componentStack: errorInfo.componentStack ?? undefined,
      pageTranslation: detectPageTranslation(),
      ...this.props.context,
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.recover();
    }
  }

  private recover() {
    this.crashLogged = false;
    this.setState({ hasError: false });
  }

  private handleRetry = () => {
    this.recover();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-16 text-center text-muted-foreground sm:px-6 lg:px-8">
          <AlertTriangle className="size-10 opacity-40" />
          <p className="max-w-md text-sm">
            Der opstod en uventet fejl ved visning af dokumentet. Prøv igen,
            eller vælg dokumentet på ny.
          </p>
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            Prøv igen
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default LegislationContentErrorBoundary;
